import React, { FC, useEffect, useRef, useState } from 'react';
import { T } from 'src/shared/helpers';
import styled from 'styled-components';
import { Icon } from 'src/components/widgets/icon';
import { ChevronIcon as ArrowDown } from 'src/components/widgets/icons/scene';
import { DropdownList, DropdownListItem, DropdownPanel } from '../../../../../../widgets/dropdown';
import { MeasurementUnit } from '../../../configurator-scene-editor/useWorkspacePreferences';
import { panelMonoFont } from '../../scene-properties-panel/panelTheme';
import { useSceneStats } from './useSceneStats';

// IL PIEDE DEL PALCO: le misure della SCENA, non della selezione — l'unità col suo ingombro, i
// vertici, i triangoli, i materiali. Una riga di soli numeri, monospazio e a cifre tabulari, così
// un valore che cambia mentre si trascina non fa ballare quelli accanto.

const Statusbar = styled.footer`
    position: absolute;
    right: 12px;
    bottom: 0;
    left: 12px;
    display: flex;
    align-items: center;
    gap: 16px;
    height: 26px;
    padding: 0 8px;
    color: ${(props) => props.theme.app.primary};
    background: ${(props) => props.theme.app.backgroundColor};
    border: 1px solid ${(props) => props.theme.app.borderColor};
    border-radius: 6px;
    font-family: ${panelMonoFont};
    font-size: 10px;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;

    b {
        font-weight: 600;
        color: ${(props) => props.theme.app.textColor};
    }
`;

// IL MENU DELL'UNITÀ STA DENTRO IL PIEDE, non in un portal sul body come il menu della piattaforma:
// col palco a schermo intero si vede solo ciò che sta dentro il palco, e un elenco appeso al body
// si aprirebbe dietro. Del menu della piattaforma prende le parti — la superficie e le voci — e si
// apre verso l'alto, perché sotto il piede non c'è più palco.
const UnitAnchor = styled.span`
    position: relative;
    display: inline-flex;
`;

const UnitButton = styled.button<{ $open: boolean }>`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    height: 20px;
    padding: 0 6px;
    border: 0;
    border-radius: 4px;
    background: ${(props) => (props.$open ? props.theme.app.pageBackgroundColor : 'transparent')};
    color: ${(props) => props.theme.app.textColor};
    font: inherit;
    cursor: pointer;

    ${Icon} {
        width: 8px;
        min-width: 8px;
        height: 8px;
        transform: rotate(${(props) => (props.$open ? '180deg' : '0deg')});
        transition: transform 160ms ease;

        svg {
            width: 8px;
            height: 8px;
        }
    }

    &:hover {
        background: ${(props) => props.theme.app.pageBackgroundColor};
    }

    &:focus-visible {
        outline: 2px solid ${(props) => props.theme.app.secondary};
        outline-offset: 1px;
    }
`;

const UnitMenu = styled(DropdownPanel)`
    position: absolute;
    bottom: calc(100% + 6px);
    left: 0;
    z-index: 1;
    min-width: 160px;
    font-family: ${(props) => props.theme.app.font};
`;

const Size = styled.span`
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const Metrics = styled.span`
    display: flex;
    gap: 16px;
    margin-left: auto;
`;

// `perMeter` è quante unità stanno in un metro, `decimals` quante cifre restano leggibili: 0.68 m
// è preciso quanto 680 mm, e un decimale di millimetro sarebbe una cifra che non significa niente.
const UNITS: Record<MeasurementUnit, { perMeter: number; decimals: number }> = {
    m: { perMeter: 1, decimals: 2 },
    cm: { perMeter: 100, decimals: 1 },
    mm: { perMeter: 1000, decimals: 0 },
    in: { perMeter: 39.3700787, decimals: 1 }
};

const UNIT_ORDER: MeasurementUnit[] = ['m', 'cm', 'mm', 'in'];

const unitLabels = (): Record<MeasurementUnit, string> => ({
    m: T._('Meters', 'Admin'),
    cm: T._('Centimeters', 'Admin'),
    mm: T._('Millimeters', 'Admin'),
    in: T._('Inches', 'Admin')
});

const count = (value: number) => value.toLocaleString('en-US');

const UNIT_MENU_ID = 'scene-statusbar-unit-menu';

interface SceneEditorStatusbarProps {
    sceneEditor?: any;
    measurementUnit: MeasurementUnit;
    onMeasurementUnitChange: (unit: MeasurementUnit) => void;
}

const SceneEditorStatusbar: FC<SceneEditorStatusbarProps> = ({ sceneEditor, measurementUnit, onMeasurementUnitChange }) => {
    const stats = useSceneStats(sceneEditor);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const anchorRef = useRef<HTMLSpanElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const labels = unitLabels();
    const unit = UNITS[measurementUnit];

    useEffect(() => {
        if (!isMenuOpen) return;

        // In cattura: il menu si chiude anche quando chi riceve il clic — la scena, un pannello —
        // lo ferma prima che salga.
        const onPointerDown = (event: PointerEvent) => {
            if (!anchorRef.current?.contains(event.target as Node)) setIsMenuOpen(false);
        };
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key !== 'Escape') return;
            setIsMenuOpen(false);
            buttonRef.current?.focus();
        };

        document.addEventListener('pointerdown', onPointerDown, true);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('pointerdown', onPointerDown, true);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [isMenuOpen]);

    const choose = (next: MeasurementUnit) => {
        onMeasurementUnitChange(next);
        setIsMenuOpen(false);
        buttonRef.current?.focus();
    };

    const size = stats.size
        ? `${[stats.size.x, stats.size.y, stats.size.z].map((value) => (value * unit.perMeter).toFixed(unit.decimals)).join(' × ')} ${measurementUnit}`
        : '—';

    return (
        <Statusbar aria-label={T._('Scene statistics', 'Admin')}>
            <UnitAnchor ref={anchorRef}>
                <UnitButton
                    ref={buttonRef}
                    type='button'
                    $open={isMenuOpen}
                    aria-expanded={isMenuOpen}
                    aria-controls={UNIT_MENU_ID}
                    aria-label={`${T._('Measurement unit', 'Admin')}: ${labels[measurementUnit]}`}
                    onClick={() => setIsMenuOpen((open) => !open)}
                >
                    {labels[measurementUnit]}
                    <Icon aria-hidden='true'>
                        <ArrowDown />
                    </Icon>
                </UnitButton>
                {isMenuOpen && (
                    <UnitMenu id={UNIT_MENU_ID}>
                        <DropdownList>
                            {UNIT_ORDER.map((id) => (
                                <DropdownListItem
                                    key={id}
                                    type='button'
                                    $active={id === measurementUnit}
                                    aria-current={id === measurementUnit}
                                    onClick={() => choose(id)}
                                >
                                    {labels[id]}
                                </DropdownListItem>
                            ))}
                        </DropdownList>
                    </UnitMenu>
                )}
            </UnitAnchor>
            <Size title={T._('Bounding box of the visible objects', 'Admin')}>{size}</Size>
            <Metrics>
                <span title={T._('Vertices', 'Admin')}>
                    {T._('Verts', 'Admin')} <b>{count(stats.vertices)}</b>
                </span>
                <span title={T._('Triangles', 'Admin')}>
                    {T._('Tris', 'Admin')} <b>{count(stats.triangles)}</b>
                </span>
                <span title={T._('Materials in use', 'Admin')}>
                    {T._('Mats', 'Admin')} <b>{count(stats.materials)}</b>
                </span>
            </Metrics>
        </Statusbar>
    );
};

export default SceneEditorStatusbar;
